import { apiUrl } from "config/envConfig";
import http from "./httpService";
import { DEFAULT_ROWS_PER_PAGE } from "constants/paginationConstants";

const path = "/order-mgmt/admin/changedriver";
const url = apiUrl;

/**
 * Fetch driver change requests with table and filtering
 *
 * @param {string} pageSize                   Page size.
 * @param {string} pageNumber                 Page number.
 * @param {string} searchBar                  Search text.
 * @param {string} status                     Request status.
 * @param {boolean|null} descendingSort       Sort by creation date descending.
 *
 * @return {Promise<Object>} Filtered driver change requests.
 */
export const getDriversRequests = async (
  pageSize = DEFAULT_ROWS_PER_PAGE,
  pageNumber = 1,
  searchBar = "",
  status = null,
  descendingSort = null // Changed from 'from' and 'to'
) => {
  let query = `?pageSize=${pageSize}&pageNumber=${pageNumber}&searchBar=${searchBar}`;
  if (status) query += `&status=${status}`;
  // Add the new sorting parameter if it is not null
  if (descendingSort !== null) {
    query += `&DescendingCreationDateSort=${descendingSort}`;
  }

  const {
    data: { data },
  } = await http.get(`${url}${path}/requests${query}`);

  return {
    page: data.page || [],
    totalRecords: data.totalRecords || 0,
  };
};

/**
 * Fetch details of files associated with a driver change.
 * @returns {Promise<Object>} The file details.
 */
export const getRequestFilesDetails = async (requestId) => {
  const {
    data: { data },
  } = await http.get(
    `${url}/order-mgmt/admin/driverchange/files/details?requestId=${requestId}`
  );
  return data;
};

/**
 * Fetch a specific file associated with a driver change request.
 *
 * @returns {Promise<Blob>} The file data as a blob.
 */
export const getRequestFile = async (fileId) => {
  const { data } = await http.get(
    `${url}/order-mgmt/admin/driverchange/file?fileId=${fileId}`,
    {
      responseType: "blob",
    }
  );
  return data;
};

/**
 * Approve a driver change request.
 *
 * @returns {Promise<Object>} Confirmation data from the server.
 */
export const approveDriverRequest = async (requestId) => {
  const { data } = await http.patch(
    `${url}/order-mgmt/admin/changedriver/approve?requestId=${requestId}`
  );
  return data;
};

/**
 * Decline a driver change request.
 *
 * @param {Object} body - The details of the decline request.
 * @param {string} body.requestId - unique identifier.
 * @param {string} [body.reason] - The reason for declining the request .
 * @returns {Promise<Object>} Confirmation data from the server.
 */
export const declineDriverRequest = async (body) => {
  const { data } = await http.patch(
    `${url}/order-mgmt/admin/changedriver/decline`,
    body
  );
  return data;
};
